import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { PageHero } from "../components/Sections";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Icon } from "../components/icons";
import { LeadForm, EnquiryDialog } from "../components/LeadForm";
import { loanProducts, cities, cityLoanSlugs } from "../mock/data";
import { CheckCircle2, FileText, ClipboardCheck, Info, ArrowRight, MapPin } from "lucide-react";

export default function LoanProduct() {
  const { slug } = useParams();
  const p = loanProducts[slug];
  if (!p) return <Navigate to="/" replace />;

  const otherProducts = Object.entries(loanProducts).filter(([s]) => s !== slug);
  const hasCityPages = cityLoanSlugs.includes(slug);

  return (
    <div>
      <PageHero title={p.hero} subtitle={p.intro} crumbs={[{ label: "Loans" }, { label: p.name }]} />

      <section className="section-pad">
        <div className="container-page grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            {p.image && (
              <div className="relative overflow-hidden rounded-2xl">
                <img src={p.image} alt={p.name} className="h-56 w-full object-cover md:h-72" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-primary shadow">
                    <Icon name={p.icon} className="h-6 w-6" />
                  </span>
                  <span className="font-heading text-2xl font-bold text-white drop-shadow">{p.name}</span>
                </div>
              </div>
            )}
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-primary">
                <Icon name={p.icon} className="h-7 w-7" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold">{p.name}</h2>
                <p className="mt-2 text-muted-foreground">{p.intro}</p>
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" /> Key Features
              </h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {p.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 rounded-lg border bg-card p-3.5">
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" /> Eligibility
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {p.eligibility.map((e) => (
                    <li key={e} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {e}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" /> Documents
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {p.documents.map((d) => (
                    <li key={d} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-secondary/50 p-4">
              <Info className="h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Please note: </strong>{p.note} Submission of an enquiry does not
                guarantee approval. The lender will disclose the applicable interest rate, APR, processing fee, taxes,
                tenure, EMI and other charges through its Key Fact Statement.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold mb-3">Explore other loans</h3>
              <div className="flex flex-wrap gap-2">
                {otherProducts.map(([s, op]) => (
                  <Link key={s} to={`/loans/${s}`}
                    className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">
                    {op.name}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-semibold mb-3">Helpful tools &amp; resources</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/calculators/emi" className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">EMI Calculator</Link>
                <Link to="/credit-score" className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">Check Credit Score</Link>
                <Link to="/faqs" className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">Loan FAQs</Link>
                <Link to="/lending-partners" className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">Lending Partners</Link>
              </div>
            </div>

            {hasCityPages && (
              <div>
                <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" /> {p.name} by city
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cities.map((c) => (
                    <Link key={c.slug} to={`/loans/${slug}/${c.slug}`}
                      className="rounded-full border px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors">
                      {p.name} in {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <Card className="sticky top-24 border p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold">Apply for {p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Get assistance with your application.</p>
              <div className="mt-4">
                <LeadForm compact defaultType={p.name} />
              </div>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
}
